import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  StaticDatabaseContext,
  type StaticDatabaseContextValue,
  useGetStaticDataSource,
} from "@-/dev.improve.haniverse.contexts.database.static";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import type { WordContextSentenceCandidate } from "#select-word-context-sentence";

/** Cap on how many candidate sentences we consider for one word card. */
export const WORD_CONTEXT_SENTENCE_LIMIT = 20;

/**
 * A word's sentence candidates, or why there are none to read:
 *
 * - `available` — read from a source that carries sentences (`candidates` may still be empty when
 *   the corpus genuinely has none for this word);
 * - `not-in-offline-data` — nothing was found in the installed level packs, which
 *   ships no sentences at all (word cards are outside the offline scope — see `lite-artifacts.ts`).
 *   The word is fine; the data is not downloaded.
 */
export type WordContextSentences =
  | { candidates: WordContextSentenceCandidate[]; kind: "available" }
  | { kind: "not-in-offline-data" };

export function useGetWordContextSentencesQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const getStaticDataSource = useGetStaticDataSource();

  return (word: string, limit: number = WORD_CONTEXT_SENTENCE_LIMIT) =>
    ["wordContextSentences", word, limit, { getStaticDataSource, staticDb }] as const;
}

/**
 * Candidate sentences containing `word`, for the sentence-context word drill.
 *
 * Reads through `sentence_word`, so this is an indexed equality on `word_id` rather than the
 * `sentence LIKE '%word%'` scan of 124k rows the example lookup used to need. The static DB is
 * immutable, so there is no table-change version in the key.
 *
 * Sentences with no translation are still returned here and filtered by
 * `selectWordContextSentence` — keeping the DB read simple and the "what is drillable" rule in
 * one place.
 *
 * An empty read decides its reason here, once, from the same load result the read went through
 * (see {@link WordContextSentences}), so the drill never re-derives it from the data source.
 */
export const [useWordContextSentencesQueryOptions, wordContextSentencesQueryFn] =
  createUseQueryOptions(
    useGetWordContextSentencesQueryKey,
    async ({
      queryKey: [_, word, limit, { getStaticDataSource, staticDb }],
    }): Promise<WordContextSentences> => {
      const candidates = await readWordContextSentenceCandidates({ limit, staticDb, word });
      if (candidates.length === 0) {
        const definition = await staticDb(import.meta.location).query.word.findFirst({
          where: { word: { eq: word } },
          with: { definitions: { columns: { definition: true }, limit: 1 } },
        });
        const translation = definition?.definitions[0]?.definition;
        if (translation) {
          return {
            kind: "available",
            candidates: [{ charOffset: 0, sentence: word, translation }],
          };
        }
      }
      if (candidates.length === 0 && (await getStaticDataSource()) === "packs") {
        return { kind: "not-in-offline-data" };
      }
      return { candidates, kind: "available" };
    },
  );

async function readWordContextSentenceCandidates({
  limit,
  staticDb,
  word,
}: {
  limit: number;
  staticDb: StaticDatabaseContextValue["db"];
  word: string;
}): Promise<WordContextSentenceCandidate[]> {
  if (word.length === 0) {
    return [];
  }

  const wordRow = await staticDb(import.meta.location).query.word.findFirst({
    columns: { id: true },
    where: { word: { eq: word } },
  });
  if (wordRow === undefined) {
    return [];
  }

  const rows = await staticDb(import.meta.location).query.sentenceWord.findMany({
    columns: { charOffset: true },
    limit,
    where: { wordId: { eq: wordRow.id } },
    with: { sentence: { columns: { sentence: true, translation: true } } },
  });

  return rows.map((row) => ({
    charOffset: row.charOffset,
    sentence: row.sentence.sentence,
    translation: row.sentence.translation,
  }));
}
